<template>
  <div>
    <loading-spinner v-if="loader" />
    <div v-else class="container-nzidoo">

      <!-- ▶ PRODUCT DETAILS PANEL -->
      <transition name="fade-slide">
        <div v-if="selectedProduct && selectedProduct.validated==null" class="details-panel" id="product-details">

          <!-- Close Button -->
          <button @click="selectedProduct = null" class="btn-close">
            <i class="mdi mdi-close"></i>
          </button>

          <div class="details-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <h2 class="details-title">
                <i class="mdi mdi-package-variant"></i>
                {{ $t("ecommerce.product.details") }}
              </h2>
              <div class="action-buttons">
                <button @click="validate(selectedProduct.id, 'validate')" class="btn-validate">
                  <i class="mdi mdi-check-circle"></i>
                  <span>{{ $t("sentence.validateOffer") }}</span>
                </button>
                <button @click="validate(selectedProduct.id, 'refuse')" class="btn-refuse">
                  <i class="mdi mdi-close-circle"></i>
                  <span>{{ $t("sentence.refuseOffer") }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="details-content">
            <div class="row">

              <!-- ▶ LEFT SIDE — IMAGES -->
              <div class="col-lg-5 mb-4">

                <!-- Seller Card -->
                <div class="seller-card">
                  <div class="seller-avatar-wrapper">
                    <img class="seller-avatar"
                      :src="selectedProduct.user.picture ? storageUrl + selectedProduct.user.picture : userpng"
                      :alt="selectedProduct.user.username" />
                    <span :class="getRoleBadgeClass(selectedProduct.user.roles)" class="seller-badge">
                      {{ getRoleLabel(selectedProduct.user.roles) }}
                    </span>
                  </div>
                  <h5 class="seller-name">{{ selectedProduct.user.firstname }} {{ selectedProduct.user.lastname }}</h5>
                  <p class="seller-username">@{{ selectedProduct.user.username }}</p>
                </div>

                <!-- Main Image -->
                <div class="main-image-wrapper">
                  <img :src="getImageUrl(selectedProduct.pictures, true)" class="main-product-image"
                    @click="showImage(getImageUrl(selectedProduct.pictures, true))" />
                </div>

                <!-- Thumbnails -->
                <div class="thumbnails-grid">
                  <div v-for="picture in getPictureArray(selectedProduct.pictures)" :key="picture"
                    class="thumbnail-item">
                    <img @click="showImage(storageUrl + picture)" class="thumbnail-image" :src="storageUrl + picture" />
                  </div>
                </div>

              </div>

              <!-- ▶ RIGHT SIDE — PRODUCT INFO -->
              <div class="col-lg-7">

                <div class="product-header">
                  <h3 class="product-name">{{ selectedProduct.name }}</h3>
                  <span :class="getSalesTypeBadge(selectedProduct.sales_type)" class="sales-type-badge">
                    {{ selectedProduct.sales_type == 'auction' ? $t("ecommerce.bids") : $t("ecommerce.fixed-price") }}
                  </span>
                </div>

                <p class="product-description">{{ selectedProduct.description }}</p>

                <!-- Pricing Info Cards -->
                <div class="info-cards-grid">
                  <div class="info-card price-card">
                    <div class="info-card-icon">
                      <i class="mdi mdi-tag"></i>
                    </div>
                    <div class="info-card-content">
                      <span class="info-label">
                        {{ selectedProduct.sales_type === 'auction' ? $t("ecommerce.product.starting-price") :
                          $t("ecommerce.fixed-price") }}
                      </span>
                      <span class="info-value">
                        {{ selectedProduct.sales_type === 'auction' ? selectedProduct.starting_price :
                          selectedProduct.fixed_price }} DA
                      </span>
                    </div>
                  </div>

                  <div class="info-card" v-if="selectedProduct.buy_it_now_price">
                    <div class="info-card-icon">
                      <i class="mdi mdi-cart"></i>
                    </div>
                    <div class="info-card-content">
                      <span class="info-label">{{ $t("ecommerce.product.buy-it-now-price") }}</span>
                      <span class="info-value">{{ selectedProduct.buy_it_now_price }} DA</span>
                    </div>
                  </div>

                  <div class="info-card" v-if="selectedProduct.lowest_price">
                    <div class="info-card-icon">
                      <i class="mdi mdi-arrow-down"></i>
                    </div>
                    <div class="info-card-content">
                      <span class="info-label">{{ $t("ecommerce.product.lowest-price") }}</span>
                      <span class="info-value">{{ selectedProduct.lowest_price }} DA</span>
                    </div>
                  </div>

                  <div class="info-card">
                    <div class="info-card-icon">
                      <i class="mdi mdi-handshake"></i>
                    </div>
                    <div class="info-card-content">
                      <span class="info-label">{{ $t("ecommerce.product.acceptOffers") }}</span>
                      <span :class="selectedProduct.accept_offer ? 'text-success' : 'text-danger'" class="info-value">
                        {{ selectedProduct.accept_offer ? $t("general.yes") : $t("general.no") }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Seller Information -->
                <div class="section-divider">
                  <h5 class="section-title">
                    <i class="mdi mdi-account-circle"></i>
                    {{ $t("actor.seller") }} {{ $t("main.information") }}
                  </h5>
                </div>

                <div class="info-list">
                  <div class="info-item">
                    <span class="info-item-label">{{ $t("attr.email") }}</span>
                    <span class="info-item-value">{{ selectedProduct.user.email }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-item-label">{{ $t("attr.username") }}</span>
                    <span class="info-item-value">@{{ selectedProduct.user.username }}</span>
                  </div>
                  <div class="info-item" v-if="selectedProduct.user.phone">
                    <span class="info-item-label">{{ $t("attr.phone") }}</span>
                    <span class="info-item-value">{{ selectedProduct.user.phone }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-item-label">{{ $t("attr.address") }}</span>
                    <span class="info-item-value">
                      {{ selectedProduct.user.address }}, {{ selectedProduct.user.city }},
                      {{ selectedProduct.user.postal_code }}, {{ selectedProduct.user.country }}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </transition>

      <page-title title="menu.list.products"/>
      <!-- ▶ PRODUCT LIST SECTION -->
      <div class="products-section">

        <div class="products-container">

          <!-- Desktop Table View -->
          <div class="table-responsive d-none d-md-block">
            <v-table class="modern-table" :data="products" :currentPage.sync="currentPage" :pageSize="10"
              @totalPagesChanged="totalPages = $event">
              <thead slot="head">
                <tr>
                  <th>{{ $t("attr.name") }}</th>
                  <th class="text-center">{{ $t("attr.pictures") }}</th>
                  <th>{{ $t("ecommerce.product.price") }}</th>
                  <th class="text-center">{{ $t("attr.units") }}</th>
                  <th class="text-center">{{ $t("general.type") }}</th>
                  <th>{{ $t("sentence.addedDate") }}</th>
                  <th class="text-center">{{ $t("main.status") }}</th>
                  <th class="text-center">{{ $t("main.action") }}</th>
                </tr>
              </thead>

              <tbody slot="body">
                <tr v-for="product in products" :key="product.id" class="product-row">
                  <td>
                    <a class="product-name-link" @click="showProductDetails(product.id)">
                      {{ product.name }}
                    </a>
                  </td>

                  <td class="text-center">
                    <img class="table-thumbnail" :src="getImageUrl(product.pictures)" />
                  </td>

                  <td>
                    <span class="price-text">{{ getProductPrice(product) }} DA</span>
                  </td>

                  <td class="text-center">
                    <span class="quantity-badge">{{ $t(`salesType.${product.sales_type}`) }}</span>
                  </td>

                  <td class="text-center">
                    <span class="quantity-badge">{{ product.quantity }}</span>
                  </td>

                  <td>
                    <span class="date-text">{{ product.updated_at | formatDate }}</span>
                  </td>

                  <td class="text-center">
                    <span class="status-badge status-pending">
                      {{ $t("main.onHold") }}
                    </span>
                  </td>

                  <td>
                    <div class="action-buttons-group">
                      <button class="btn-action btn-view" @click="showProductDetails(product.id)" title="View">
                        <i class="mdi mdi-eye"></i>
                      </button>
                      <button class="btn-action btn-approve" @click="validate(product.id, 'validate')" title="Validate">
                        <i class="mdi mdi-check"></i>
                      </button>
                      <button class="btn-action btn-reject" @click="validate(product.id, 'refuse')" title="Refuse">
                        <i class="mdi mdi-close"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- Mobile Card View -->
          <div class="mobile-cards d-md-none">
            <div v-for="product in products" :key="product.id" class="product-card-mobile">
              <div class="product-card-header">
                <img class="product-card-image" :src="getImageUrl(product.pictures)" />
                <div class="product-card-info">
                  <h5 class="product-card-title" @click="showProductDetails(product.id)">
                    {{ product.name }}
                  </h5>
                  <p class="product-card-price">{{ getProductPrice(product) }} DA</p>
                  <span class="status-badge status-pending">{{ $t("main.onHold") }}</span>
                </div>
              </div>
              <div class="product-card-footer">
                <button class="btn-mobile-action btn-view-mobile" @click="showProductDetails(product.id)">
                  <i class="mdi mdi-eye"></i> {{ $t("action.view") }}
                </button>
                <button class="btn-mobile-action btn-approve-mobile" @click="validate(product.id, 'validate')">
                  <i class="mdi mdi-check"></i> {{ $t("action.validate") }}
                </button>
                <button class="btn-mobile-action btn-reject-mobile" @click="validate(product.id, 'refuse')">
                  <i class="mdi mdi-close"></i> {{ $t("action.refuse") }}
                </button>
              </div>
            </div>
          </div>

          <!-- Pagination or No Results -->
          <div class="pagination-wrapper">
            <smart-pagination v-if="products.length > 0" :currentPage.sync="currentPage" :totalPages="totalPages" />
            <div v-else class="no-results">
              <i class="mdi mdi-inbox"></i>
              <p>{{ $t("sentence.noNewProductsForValidation") }}</p>
            </div>
          </div>

        </div>
      </div>

      <ProductDetailsModal :picture="selectedPicture" />
    </div>
  </div>
</template>

<script>
import config from "@/config";
import ProductDetailsModal from "@/components/modals/PictureDetailsModal";
import LoadingSpinner from '@/components/templates/LoadingSpinner.vue';
import userpng from '@/assets/img/user/user.webp';
import PageTitle from '@/components/templates/PageTitle.vue';

export default {
  name: "NewProductList",

  mounted() {
    this.getNewProduct();
    if (this.$route.params.id) {
      this.showProductDetails(this.$route.params.id);
    }
  },

  data() {
    return {
      userpng:userpng,
      products: [],
      currentPage: 1,
      totalPages: 0,
      selectedProduct: null,
      loader: false,
      storageUrl: config.storageUrl,
      selectedPicture: "",
    };
  },

  components: {
    ProductDetailsModal,
    LoadingSpinner,
    PageTitle,
  },

  filters: {
    formatDate(date) {
      if (!date) return '';
      const options={ year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(date).toLocaleDateString(undefined, options);
    }
  },

  methods: {
    getImageUrl(pictures, isMain=false) {
      let url;
      if (Array.isArray(pictures)&&pictures.length>0) {
        url=pictures[0];
      } else if (typeof pictures==='string') {
        url=pictures;
      } else {
        return isMain? '/default-product-main.png':'/default-product.png';
      }
      return this.storageUrl+url;
    },

    getProductPrice(product) {
      return product.sales_type==='auction'
        ? product.starting_price
        :product.fixed_price;
    },

    getPictureArray(pictures) {
      if (Array.isArray(pictures)) {
        return pictures;
      } else if (typeof pictures==='string'&&pictures) {
        return [pictures];
      }
      return [];
    },

    getStatusBadgeClass() {
      return 'badge badge-warning';
    },

    getSalesTypeBadge(type) {
      return type==='auction'? 'badge-auction':'badge-fixed';
    },

    getRoleLabel(roles) {
      if (roles.includes('ROLE_SELLER')) {
        return this.$t('actor.seller');
      }
      if (roles.includes('ROLE_CLIENT')) {
        return this.$t('actor.client');
      }
      return this.$t('general.user');
    },

    getRoleBadgeClass(roles) {
      if (roles.includes('ROLE_SELLER')) {
        return 'role-badge-seller';
      }
      return 'role-badge-client';
    },

    showImage(picture) {
      this.selectedPicture=picture;
      this.$modal.show("picture-details");
    },

    async getNewProduct() {
      try {
        const response=await this.$axios.get("admin/product/getNonValidated");
        this.products=response.data;
      } catch (err) {
        console.error("Error fetching non-validated products:", err.response.data);
      }
    },

    async validate(id, responseType) {
      try {
        this.loader=true;
        await this.$axios.post(`admin/product/${responseType}/${id}`);
        this.selectedProduct=null;
        this.getNewProduct();
      } catch (err) {
        console.error(`Error ${responseType}ing product ${id}:`, err);
      } finally {
        this.loader=false;
      }
    },

    async showProductDetails(id) {
      try {
        const response=await this.$axios.get("product/get/"+id);
        this.selectedProduct=response.data;
        this.scrollToClass("product-details");
      } catch (err) {
        console.error("Error fetching product details:", err);
      }
    },

    scrollToClass(className) {
      const el=this.$el.querySelector(`#${className}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    },
  },
};
</script>

<style scoped src="@/assets/css/table-admin-styles.css"></style>
